import React, { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import ConnectionStatus from "./components/ConnectionStatus";

// Pages
import LandingPage from "./pages/LandingPage";
import DashboardPage from "./pages/DashboardPage";
import TrackRecordPage from "./pages/TrackRecordPage";
import PricingPage from "./pages/PricingPage";
import HammerTalkPage from "./pages/HammerTalkPage";
import HammerCollectionPage from "./pages/HammerCollectionPage";
import MasterclassPage from "./pages/MasterclassPage";
import AdminPage from "./pages/AdminPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import UpdatesPage from "./pages/UpdatesPage";
import RoadTo100KPage from "./pages/RoadTo100KPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios to send credentials (cookies) with all requests
axios.defaults.withCredentials = true;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check auth on initial mount - try cookie first, then localStorage fallback
  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = localStorage.getItem("hsl_token");
      try {
        // Try cookie-based auth first (withCredentials sends cookies)
        // If that fails and we have a saved token, use Authorization header
        const headers = savedToken ? { Authorization: `Bearer ${savedToken}` } : {};
        const response = await axios.get(`${API}/auth/me`, { headers });
        setUser(response.data);
      } catch (error) {
        // Not authenticated - clear any stale token
        localStorage.removeItem("hsl_token");
        setUser(null);
      }
      setLoading(false);
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    // Store token in localStorage as fallback for cookie issues
    localStorage.setItem("hsl_token", access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { access_token, user: userData } = response.data;
    // Store token in localStorage as fallback
    localStorage.setItem("hsl_token", access_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      // Ignore errors
    }
    localStorage.removeItem("hsl_token");
    setUser(null);
    toast.success("Logged out successfully");
  };

  const updateNotifications = async (prefs) => {
    if (!user) return;
    
    try {
      const savedToken = localStorage.getItem("hsl_token");
      const headers = savedToken ? { Authorization: `Bearer ${savedToken}` } : {};
      const response = await axios.put(`${API}/auth/notifications`, prefs, { headers });
      setUser(prev => ({ 
        ...prev, 
        email_notifications: response.data.email_notifications,
        sms_notifications: response.data.sms_notifications,
        phone_number: response.data.phone_number,
        digest_preference: response.data.digest_preference
      }));
      toast.success("Notification preferences updated!");
      return response.data;
    } catch (error) {
      toast.error("Failed to update notification preferences");
      throw error;
    }
  };

  // Check if user is authenticated (for components that need this info)
  const isAuthenticated = !!user;
  const token = localStorage.getItem("hsl_token");

  return (
    <AuthContext.Provider value={{ user, setUser, token, loading, login, register, logout, updateNotifications, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

// Layout Component
const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-hsl-bg">
      <div className="noise-overlay" />
      {children}
      <ConnectionStatus />
      <Toaster 
        theme="dark" 
        position="top-right"
        toastOptions={{
          style: {
            background: '#121214',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff'
          }
        }}
      />
    </div>
  );
};

// Google Analytics page view tracker for SPA navigation
const GAPageTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Send page view to GA4 on route change
    if (window.gtag) {
      window.gtag('config', 'G-RMZ18PD9HL', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);
  
  return null;
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <GAPageTracker />
          <Layout>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/picks" element={<DashboardPage />} />
              <Route path="/track-record" element={<TrackRecordPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/hammer-talk" element={<HammerTalkPage />} />
              <Route path="/hammer-collection" element={<HammerCollectionPage />} />
              <Route path="/masterclass" element={<MasterclassPage />} />
              <Route path="/road-to-100k" element={<RoadTo100KPage />} />
              <Route path="/updates" element={<UpdatesPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
            </Routes>
          </Layout>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
